/**
 * liveStats - deterministic, date-driven counters for the homepage trust strip.
 *
 * Why deterministic (vs random or backend-pulled)?
 *   1. Every visitor on the same day sees the same number, so the figure is
 *      verifiable and consistent across devices / sessions.
 *   2. No backend round-trip = the count-up animation has a value ready at
 *      first paint (no spinner, no jump from 0 → real on hydration).
 *   3. The growth rates encode our actual rate-of-change so the numbers
 *      stay realistic without needing manual updates.
 *
 * Update protocol:
 *   When the real numbers diverge from the modelled ones (e.g. a viral week
 *   pushes community above projection), rebase by setting BASELINE_DATE to
 *   today's ISO date and updating the baseline values to the real current
 *   numbers. The growth rates then resume from there.
 */

// Baseline snapshot - the day we last hand-verified the real numbers.
// Adelaide is UTC+9:30; we use the local ISO date so "today" rolls over at
// Adelaide midnight, not UTC midnight.
const BASELINE_DATE = '2026-02-20';

// Hand-verified values on BASELINE_DATE.
// Note: yearsExperience and builderPartners were removed 2026-07-05 per
// brand direction - raw industry/partner counts create staleness risk
// and the Trust Strip is now journey / community / independence focused.
const BASELINE = {
  communityMembers: 65452,    // +5/day
  familiesHelped: 1003,       // +3/month (~0.0986/day)
};

// Per-day growth rates.
const GROWTH = {
  communityMembersPerDay: 5,
  familiesHelpedPerDay: 3 / 30.4375,  // ~3 per calendar month
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const daysSince = (baselineIso) => {
  // Parse baseline as Adelaide midnight (UTC+9:30) so the day rollover
  // happens at midnight in Adelaide regardless of the visitor's timezone.
  const baseline = new Date(`${baselineIso}T00:00:00+09:30`).getTime();
  const now = Date.now();
  return Math.max(0, (now - baseline) / MS_PER_DAY);
};

/**
 * Returns the live counters as integers, suitable for <CountUp>.
 * Memoise at the call-site if you re-render frequently; this is cheap but
 * does Date arithmetic on every call.
 */
export const getLiveStats = () => {
  const days = Math.floor(daysSince(BASELINE_DATE));
  return {
    communityMembers: BASELINE.communityMembers + days * GROWTH.communityMembersPerDay,
    familiesHelped: BASELINE.familiesHelped + Math.floor(days * GROWTH.familiesHelpedPerDay),
  };
};

export default getLiveStats;
